const getDemoDate = (month, year, day) =>
  new Date(Number(year), Number(month) - 1, day).toISOString();

export const demoExpenseTemplates = [
  { text: "Apartment Rent", amount: 32000, category: "rent", source: "rule", day: 2 },
  { text: "Office Lunches", amount: 8500, category: "food", source: "ai", day: 4 },
  { text: "Metro and Fuel", amount: 7200, category: "transport", source: "rule", day: 6 },
  { text: "Monthly Grocery Run", amount: 13500, category: "grocery", source: "ocr", day: 8 },
  { text: "Workwear Shopping", amount: 9800, category: "shopping", source: "ai", day: 10 },
  { text: "Electricity and Internet", amount: 6200, category: "utilities", source: "ocr", day: 12 },
  { text: "Streaming and Movies", amount: 4300, category: "entertainment", source: "rule", day: 14 },
  { text: "Clinic and Medicines", amount: 5700, category: "medical", source: "ocr", day: 16 },
  { text: "Course Subscription", amount: 7600, category: "education", source: "ai", day: 18 },
  { text: "Coffee and Beverages", amount: 3200, category: "beverage", source: "rule", day: 20 },
  { text: "Miscellaneous Essentials", amount: 2500, category: "others", source: "rule", day: 22 },
];

export const demoIncomeTemplates = [
  { source: "Salary", amount: 125000, note: "Monthly salary", day: 1 },
  { source: "Freelance", amount: 25000, note: "Consulting payout", day: 11 },
];

export const demoBudgetTemplates = [
  { category: "rent", amount: 32000 },
  { category: "food", amount: 9000 },
  { category: "transport", amount: 8000 },
  { category: "grocery", amount: 14000 },
  { category: "shopping", amount: 9000 },
  { category: "utilities", amount: 6500 },
  { category: "entertainment", amount: 5000 },
  { category: "medical", amount: 6000 },
  { category: "education", amount: 8000 },
  { category: "beverage", amount: 3000 },
  { category: "others", amount: 3500 },
];

export function getDemoExpenses(month, year) {
  return demoExpenseTemplates.map((expense, index) => ({
    _id: `demo-expense-${index + 1}`,
    ...expense,
    month: Number(month),
    year: Number(year),
    createdAt: getDemoDate(month, year, expense.day),
  }));
}

export function getDemoIncome(month, year) {
  return demoIncomeTemplates.map((income, index) => ({
    _id: `demo-income-${index + 1}`,
    ...income,
    month: Number(month),
    year: Number(year),
    createdAt: getDemoDate(month, year, income.day),
  }));
}

export function getDemoBudgets(month, year) {
  return demoBudgetTemplates.map((budget, index) => ({
    _id: `demo-budget-${index + 1}`,
    ...budget,
    month: Number(month),
    year: Number(year),
    createdAt: getDemoDate(month, year, 1),
  }));
}

export function getDemoCategoryBreakdown(expenses) {
  return expenses.reduce((totals, expense) => {
    const category = expense.category || "others";
    totals[category] = (totals[category] || 0) + Number(expense.amount || 0);
    return totals;
  }, {});
}

export function getDemoDashboardData(month, year) {
  const expenses = getDemoExpenses(month, year);
  const incomes = getDemoIncome(month, year);
  const budgets = getDemoBudgets(month, year);
  const totalSpend = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const categoryBreakdown = getDemoCategoryBreakdown(expenses);
  const topCategoryEntry = Object.entries(categoryBreakdown).sort((a, b) => b[1] - a[1])[0];
  const previousTotal = 92500;
  const predictedTotal = 106000;

  return {
    expenses,
    incomes,
    budgets,
    prediction: {
      predictedTotal,
      averageMonthlySpend: 98000,
      highestCategory: topCategoryEntry?.[0] || "rent",
      trend: "stable",
      message: "Demo forecast expects next month around Rs 1,06,000. Watch rent, grocery, and shopping.",
      categoryPrediction: {
        rent: 32000,
        food: 9000,
        transport: 7600,
        grocery: 14500,
        shopping: 10500,
        utilities: 6500,
        entertainment: 4500,
        medical: 6000,
        education: 7800,
        beverage: 3500,
        others: 2600,
      },
    },
    monthlyComparison: {
      selectedMonth: Number(month),
      selectedYear: Number(year),
      previousMonth: Number(month) === 1 ? 12 : Number(month) - 1,
      previousYear: Number(month) === 1 ? Number(year) - 1 : Number(year),
      currentTotal: totalSpend,
      previousTotal,
      difference: totalSpend - previousTotal,
      percentageChange: ((totalSpend - previousTotal) / previousTotal) * 100,
      trend: totalSpend > previousTotal ? "increased" : "decreased",
      message: "Demo spending is higher than last month because grocery and shopping increased.",
    },
    aiInsights: {
      totalSpend,
      topCategory: topCategoryEntry?.[0] || "rent",
      topAmount: topCategoryEntry?.[1] || 0,
      expenseCount: expenses.length,
      categoryBreakdown,
      insight:
        "Demo Mode: fixed rent is under control, grocery is near budget, and shopping is slightly above plan.",
    },
  };
}

const now = new Date();
export const demoExpenses = getDemoExpenses(now.getMonth() + 1, now.getFullYear());
export const demoIncome = getDemoIncome(now.getMonth() + 1, now.getFullYear());
export const demoBudgets = getDemoBudgets(now.getMonth() + 1, now.getFullYear());

const categoryRules = [
  { category: "rent", words: ["rent", "apartment", "flat"] },
  { category: "grocery", words: ["grocery", "groceries", "vegetable", "milk"] },
  { category: "food", words: ["food", "lunch", "dinner", "biryani", "restaurant"] },
  { category: "transport", words: ["fuel", "petrol", "diesel", "metro", "cab", "taxi", "bus"] },
  { category: "shopping", words: ["shopping", "shirt", "clothes", "amazon"] },
  { category: "utilities", words: ["electricity", "internet", "wifi", "water", "bill"] },
  { category: "entertainment", words: ["movie", "netflix", "streaming", "game"] },
  { category: "medical", words: ["medical", "medicine", "doctor", "clinic"] },
  { category: "education", words: ["course", "school", "tuition", "book"] },
  { category: "beverage", words: ["coffee", "tea", "juice", "beverage"] },
];

export function categorizeDemoExpense(text) {
  const normalizedText = String(text || "").toLowerCase();
  return (
    categoryRules.find((rule) =>
      rule.words.some((word) => normalizedText.includes(word))
    )?.category || "others"
  );
}

export function parseDemoExpenseText(text, source = "ai") {
  return String(text || "")
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const amountMatch = entry.match(/(?:rs\.?|inr|₹)?\s*(\d+(?:\.\d+)?)/i);
      const amount = Number(amountMatch?.[1] || 0);
      const label = entry
        .replace(amountMatch?.[0] || "", "")
        .replace(/\s+/g, " ")
        .trim();

      return amount > 0
        ? {
            text: label || entry,
            amount,
            category: categorizeDemoExpense(entry),
            source,
          }
        : null;
    })
    .filter(Boolean);
}
